import React, { forwardRef, ButtonHTMLAttributes, ReactNode } from "react";

/**
 * PillButtonProps defines the props for the PillButton component.
 */
export interface PillButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button content */
  children: ReactNode;
  /** Visual variant */
  variant?: "primary" | "secondary" | "dark" | "basic";
  /** Size of the button */
  size?: "sm" | "md" | "lg" | "xl";
  /** Custom className */
  className?: string;
  /** Custom style */
  style?: React.CSSProperties;
}

// Style mappings for variants and sizes
const VARIANT_STYLES: Record<string, string> = {
  primary:
    "rounded-full bg-[#043A53] text-white border-gray-800 text-gray-800 hover:bg-gray-600 hover:text-white dark:border-gray-200 dark:text-gray-200 dark:hover:bg-gray-200 dark:hover:text-black",
  secondary:
    "rounded-full bg-[#F0B73F] text-black font-bold opacity-900 border-gray-500 text-gray-500 hover:bg-gray-500 hover:text-white dark:border-gray-400 dark:text-gray-400 dark:hover:bg-gray-400 dark:hover:text-black",
  dark:
    "rounded-full hover:shadow-lg hover:text-gray-300 dark:bg-gray-900 dark:border-gray-900 dark:text-gray-300 dark:hover:text-white",
  basic:
    "rounded-full text-gray-800 hover:shadow-lg hover:text-gray-600 dark:text-white dark:hover:text-gray-300",
};

const SIZE_STYLES: Record<string, string> = {
  sm: "p-1 px-4 text-sm",
  md: "p-2 px-5 text-base",
  lg: "p-4 px-6 text-base",
  xl: "p-6 px-8 text-lg",
};

/**
 * PillButton is a flexible, reusable button component styled as a pill.
 * Supports multiple variants and sizes, and is accessible.
 */
const PillButton = forwardRef<HTMLButtonElement, PillButtonProps>(
  (
    {
      children,
      variant = "dark",
      size = "md",
      className = "",
      style,
      disabled = false,
      type = "button",
      ...rest
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        style={style}
        className={`border transition duration-300 ease-in-out 
          ${VARIANT_STYLES[variant]} 
          ${SIZE_STYLES[size]} 
          ${disabled ? "opacity-50 cursor-not-allowed" : ""} 
          ${className}`}
        {...rest}
      >
        {children}
      </button>
    );
  }
);

PillButton.displayName = "PillButton";

export default PillButton;
